import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Lock, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { user, login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) {
      toast.success("Connexion réussie");
      navigate("/");
    } else {
      toast.error("Connexion échouée");
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - branding */}
      <div className="hidden md:flex md:w-1/2 text-white p-12 flex-col justify-between relative overflow-hidden" style={{ backgroundColor: "#1F5F5B" }}>
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-orange-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 -left-10 w-60 h-60 bg-amber-400/15 rounded-full blur-3xl" />

        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#1F5F5B] border border-white/15 flex items-center justify-center shadow-lg shadow-black/40 overflow-hidden">
            <img src="/logo.png" alt="rimmoGET" className="w-12 h-12 object-cover" />
          </div>
          <div>
            <div className="font-heading font-bold text-xl">rimmoGET</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300">Gestion locative</div>
          </div>
        </div>

        <div className="relative">
          <div className="text-xs uppercase tracking-[0.22em] text-amber-300/90 font-bold mb-4">
            Plateforme professionnelle
          </div>
          <h2 className="font-heading text-4xl lg:text-5xl font-bold leading-tight mb-4">
            Gérez vos biens, locataires et paiements depuis un seul tableau de bord.
          </h2>
          <p className="text-teal-100/80 leading-relaxed max-w-md">
            Suivi en temps réel des loyers, dépenses et occupation. Pensé pour les gestionnaires
            de location immobilière en zone FCFA.
          </p>
        </div>

        <div className="relative text-xs text-teal-200/60" data-testid="login-copyright">
          © 2026 copyright <span className="text-amber-300/90 font-medium">rpstogo</span>
        </div>
      </div>

      {/* Right side - form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-white">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md space-y-6"
          data-testid="login-form"
        >
          {/* Mobile-only brand header */}
          <div className="md:hidden flex items-center gap-3 pb-4 border-b border-slate-100" data-testid="login-mobile-brand">
            <div className="w-11 h-11 rounded-xl bg-[#1F5F5B] border border-teal-700/20 flex items-center justify-center shadow-md overflow-hidden">
              <img src="/logo.png" alt="rimmoGET" className="w-11 h-11 object-cover" />
            </div>
            <div>
              <div className="font-heading font-bold text-slate-900 text-lg leading-none">rimmoGET</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber-600 mt-1.5">Gestion locative</div>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700/80 mb-3">Bienvenue</div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Se connecter</h1>
            <p className="text-slate-500 mt-2 text-sm">Accédez à votre espace de gestion locative.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 text-sm">Adresse e-mail</Label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-9 h-11"
                  data-testid="login-email-input"
                  placeholder="votre@email.com"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 text-sm">Mot de passe</Label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-9 h-11"
                  data-testid="login-password-input"
                  placeholder="••••••••"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white font-medium shadow-md shadow-orange-900/20"
          >
            {loading ? "Connexion…" : "Se connecter"}
          </Button>

          <div className="pt-2 text-sm text-center text-slate-600">
            Vous n'avez pas encore de compte&nbsp;?{" "}
            <Link
              to="/signup"
              className="font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-4 decoration-orange-300 hover:decoration-orange-600 transition-colors"
              data-testid="login-signup-link"
            >
              Créez un compte
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
